import { expandedState } from '@atlaskit/editor-common/expand';
import { processRawValue } from '@atlaskit/editor-common/process-raw-value';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { Transform } from '@atlaskit/editor-prosemirror/transform';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { Suggestion, ToolCall } from '../../../aiSuggestionsPluginType';
import { parseToolCallAttrs } from './parseToolCallAttrs';
import { safeInsertAtPos } from './safeInsertAtPos';
import { wrapNode } from './wrapNode';

type JsonObject = Record<string, unknown>;

function isJsonObject(value: unknown): value is JsonObject {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clearMentionText(_key: string, value: unknown): unknown {
	if (!isJsonObject(value)) {
		return value;
	}

	const { attrs } = value;
	if (
		value.type !== 'mention' ||
		!isJsonObject(attrs) ||
		typeof attrs.text !== 'string' ||
		attrs.text.length === 0
	) {
		return value;
	}

	return { ...value, attrs: { ...attrs, text: '' } };
}

function createNodeFromToolCallValue(schema: Schema, value: string): PMNode | undefined {
	try {
		const parsed =
			typeof value === 'string'
				? JSON.parse(
						value,
						fg('platform_editor_ai_show_diff_patch_2') ? clearMentionText : undefined,
					)
				: value;
		return processRawValue(schema, parsed);
	} catch {
		return undefined;
	}
}

export function getPositionsByLocalId(
	doc: PMNode,
	localId: string,
): { from: number; to: number } | undefined {
	let result: { from: number; to: number } | undefined;
	doc.descendants((node, pos) => {
		if (result) {
			return false;
		}
		if (node.attrs.localId === localId) {
			result = { from: pos, to: pos + node.nodeSize };
		}
		return !result;
	});
	return result;
}

const EXPAND_NODE_TYPE_NAMES = ['expand', 'nestedExpand'];

/**
 * Finds the node of `typeName` that now encloses the node with `localId`, i.e. the
 * wrapper a `wrap` tool call just created around it. Re-deriving it from the tool call's
 * own `localId` avoids having to track the wrapper's position across the steps of the
 * tool calls that follow, which can both move it and replace the node object at it.
 */
function findWrapperOfNode(doc: PMNode, localId: string, typeName: string): PMNode | undefined {
	const positions = getPositionsByLocalId(doc, localId);
	if (!positions) {
		return undefined;
	}
	const $pos = doc.resolve(positions.from);
	for (let depth = $pos.depth; depth > 0; depth--) {
		const ancestor = $pos.node(depth);
		if (ancestor.type.name === typeName) {
			return ancestor;
		}
	}
	return undefined;
}

/**
 * Opt every `expand` / `nestedExpand` this suggestion wrapped content in into being
 * open, so wrapping content in an expand doesn't collapse it out of sight.
 *
 * The single-player expand implementation keeps open/closed state in the
 * `expandedState` WeakMap keyed by node identity — the `__expanded` attr is only read
 * by the legacy implementation, and its schema default of `true` has no effect here.
 * A wrapper created by `wrapNode` therefore has no entry, and `ExpandNodeView` defaults
 * an unknown node to collapsed. Every other expand-creating path (`insertExpand`, the
 * block menu's convert-to-expand) opts its new node in the same way.
 *
 * Deliberately runs against the final doc of the transform, after every tool call has
 * been applied: the entry is keyed on the node object, so it has to be set against the
 * object that survives — a later tool call editing inside a wrapper replaces it.
 */
function openWrappedExpands(tr: Transaction | Transform, suggestion: Suggestion): void {
	if (!fg('platform_editor_ai_show_diff_patch_1')) {
		return;
	}
	for (const toolCall of suggestion.toolCalls ?? []) {
		if (toolCall.name !== 'wrap' && toolCall.name !== 'wrapNode') {
			continue;
		}
		const { localId, wrapperType } = toolCall.arguments;
		if (!EXPAND_NODE_TYPE_NAMES.includes(wrapperType)) {
			continue;
		}
		// Matching on the requested `wrapperType` keeps a failed wrap from opening a
		// pre-existing expand that merely happened to already contain the target.
		const wrapper = findWrapperOfNode(tr.doc, localId, wrapperType);
		if (wrapper) {
			expandedState.set(wrapper, true);
		}
	}
}

function applyToolCall(tr: Transaction | Transform, toolCall: ToolCall): void {
	if (toolCall.name === 'removeNodes') {
		for (const id of toolCall.arguments.localIds) {
			const positions = getPositionsByLocalId(tr.doc, id);
			if (positions) {
				tr.delete(positions.from, positions.to);
			}
		}
		return;
	}

	const positions = getPositionsByLocalId(tr.doc, toolCall.arguments.localId);

	if (!positions) {
		return;
	}

	switch (toolCall.name) {
		case 'replaceNode':
		case 'insertNodeBefore':
		case 'insertNodeAfter': {
			const node = createNodeFromToolCallValue(tr.doc.type.schema, toolCall.arguments.value);
			if (!node) {
				return;
			}
			// An attribute-only replaceNode (e.g. promoting a heading from H4 to H3) keeps the
			// node's type and content identical. Encoding it as a full replace yields a ReplaceStep
			// whose granular diff is empty (the text is unchanged), so the change never surfaces.
			// Apply it via setNodeMarkup instead so the diff plugin renders it as a node change.
			const existingNode = tr.doc.nodeAt(positions.from);
			if (
				toolCall.name === 'replaceNode' &&
				existingNode &&
				existingNode.type === node.type &&
				existingNode.content.eq(node.content)
			) {
				tr.setNodeMarkup(positions.from, undefined, node.attrs, node.marks);
				return;
			}
			if (toolCall.name === 'replaceNode') {
				/**
				 *  Only fall back to delete + safeInsertAtPos
				 * (which walks up the doc to find the nearest schema-valid ancestor) when a
				 * direct replace would produce invalid ProseMirror doc nesting.
				 *
				 * canReplaceWith takes in indices, so for replaceNode we only need to check the
				 * from index and the next index since this is for replacing a single node.
				 */
				const $from = tr.doc.resolve(positions.from);
				const canReplaceDirectly = $from.parent.canReplaceWith(
					$from.index(),
					$from.index() + 1,
					node.type,
				);
				if (canReplaceDirectly) {
					tr.replaceWith(positions.from, positions.to, node);
					return;
				}
				tr.delete(positions.from, positions.to);
				safeInsertAtPos(tr, positions.from, node);
				return;
			}
			const insertPos = toolCall.name === 'insertNodeAfter' ? positions.to : positions.from;
			const side = toolCall.name === 'insertNodeBefore' ? 'before' : 'after';
			safeInsertAtPos(tr, insertPos, node, side);
			return;
		}
		case 'removeNode':
			tr.delete(positions.from, positions.to);
			return;
		case 'wrap':
		case 'wrapNode':
			wrapNode({
				toolCallArguments: toolCall.arguments,
				tr,
				pos: positions.from,
			});
			return;
		case 'setAttrs': {
			const node = tr.doc.nodeAt(positions.from);
			if (!node) {
				return;
			}
			// The payload may have encoded the attrs as a JSON string, which `setNodeMarkup` would
			// spread as junk keys and apply none of. See `parseToolCallAttrs`.
			const attrs = parseToolCallAttrs(toolCall.arguments.attrs);
			// Nothing to apply: skip rather than re-set the node's current attrs, which would add
			// a step the diff then has to render as a change that changed nothing.
			if (!attrs) {
				return;
			}
			tr.setNodeMarkup(positions.from, undefined, {
				...node.attrs,
				...attrs,
			});
			return;
		}
	}
}

export function applyToolCalls<T extends Transaction | Transform>({
	tr,
	suggestion,
}: {
	suggestion: Suggestion;
	tr: T;
}): T {
	for (const toolCall of suggestion.toolCalls ?? []) {
		applyToolCall(tr, toolCall);
	}
	openWrappedExpands(tr, suggestion);
	return tr;
}
